<template>
  <slot name="trigger" :openModal="openModal"></slot>
  <BaseModal :actionRequired="actionRequired" :additionalStyles="additionalStyles" @update:state="childEmits">
    <template #modal-content>
      <slot
        name="modal-content"
        :closeModal="closeModal"
        :closeRedirect="closeRedirect"
      ></slot>
    </template>
  </BaseModal>
</template>

<script setup>
import { onMounted, provide, ref, watch, inject } from 'vue';
import BaseModal from '../BaseModal/BaseModal.vue';
import { useCookies } from '../../hooks/useCookies';

const { getCookie, setCookie, expireCookie } = useCookies();

const isOpen = ref(false);
const canBeOpened = ref(true);
const cookieIsSaveable = ref(false);
const injectedIsOpen = inject(props?.injectionName);

const openModal = (e) => {
  if (canBeOpened.value === true) {
    isOpen.value = true;
  }
};

const closeModal = (e) => {
  isOpen.value = false;
  let expirationDate = new Date();
  expirationDate.setTime(
    expirationDate.getTime() + 10 * 365 * 24 * 60 * 60 * 1000,
  );
  setCookie(props.cookieName, expirationDate, props.lastUpdated);
};

const closeRedirect = (e) => {
  e.preventDefault();
  isOpen.value = false;
  let expirationDate = new Date();
  expirationDate.setTime(
    expirationDate.getTime() + 10 * 365 * 24 * 60 * 60 * 1000,
  );
  setCookie(props.cookieName, expirationDate, props.lastUpdated);
  window.location = e.target.getAttribute('href');
};

const childEmits = (value) => {
  if (value === 'isClosed') {
    closeModal();
  }
};

const props = defineProps({
  cookieName: {
    type: String,
    default: '',
  },
  lastUpdated: {
    type: String,
    default: '',
  },
  openOnMount: {
    type: Boolean,
    default: false,
  },
  removeCookie: {
    type: Boolean,
    default: false,
  },
  injectionName: {
    type: String,
    default: 'injectedIsOpen',
  },
  actionRequired: {
    type: Boolean,
    default: false,
  },
  additionalStyles: {
    type: Object,
    default: () => {
      return { color: 'red', fontSize: '13px' };
    },
  }
});

provide('injectedIsOpen', isOpen);

onMounted(() => {
  const { cookieName, lastUpdated, openOnMount, removeCookie } = props;
  const cookie = getCookie(props.cookieName);

  // If it is flagged to expire its cookie we remove it
  if (removeCookie && cookieName) {
    expireCookie(cookieName);
  }

  // First we check if it was accepted / dismissed, if it has and the modal hasnt been refreshed we cut this short and exit
  if (cookie && cookie === props.lastUpdated) {
    canBeOpened.value = false;
    return;
  }

  // Next we check if it all the props required to make this a once off model is available
  if (cookieName && lastUpdated && cookieName !== '' && lastUpdated !== '') {
    cookieIsSaveable.value = true;
  } else {
    // If not we exit
    return;
  }

  // Next we remove the old cookie, if it gets to this point
  if (cookieName !== '' && lastUpdated !== '' && cookie !== props.lastUpdated) {
    expireCookie(cookieName);
  }

  // Next we check if the modal is supposed to be open on mount and if we should open it
  if (openOnMount && canBeOpened.value === true) {
    isOpen.value = true;
  }
});

watch(
  () => injectedIsOpen?.value,
  () => {
    isOpen.value = injectedIsOpen.value;
  },
);
</script>
